import {getCurrentCompany, getStoredCompanyList} from './company'
import {reportHandledError} from './sentry'

export const SUBSCRIPTION_ACCESS_ALLOWED = 'allowed';
export const SUBSCRIPTION_ACCESS_PRO_REQUIRED = 'proRequired';
export const SUBSCRIPTION_ACCESS_PREMIUM_REQUIRED = 'premiumRequired';
const SUBSCRIPTION_PAGE_CONTEXT_KEY = 'swiftInvoiceSubscriptionContext';

function requireLoginInformation() {
    const stored = localStorage.getItem('loginInformation');
    if (stored == null || stored === '') {
        return {};
    }
    try {
        return JSON.parse(stored).objectData || {};
    } catch (error) {
        reportHandledError(error, {
            module: 'subscription',
            operation: 'read_login_information'
        });
        return {};
    }
}

function currentTimestampSeconds() {
    return Math.floor(Date.now() / 1000);
}

function timestampSeconds(value) {
    return Number(value == null || value === '' ? 0 : value);
}

export function isActiveTrial(loginInformation) {
    return loginInformation != null
        && Number(loginInformation.iapStatus) === 2
        && timestampSeconds(loginInformation.expiresTime) > currentTimestampSeconds();
}

export function getTrialDaysRemaining(loginInformation) {
    if (!isActiveTrial(loginInformation)) {
        throw new Error('An active trial is required');
    }
    return Math.ceil((timestampSeconds(loginInformation.expiresTime) - currentTimestampSeconds()) / 86400);
}

export function hasSubscriptionAccess(loginInformation) {
    const iapStatus = Number(loginInformation == null ? NaN : loginInformation.iapStatus);
    const expiresTime = Number(loginInformation == null ? NaN : loginInformation.expiresTime);
    return [2, 3, 4, 5].includes(iapStatus)
        && Number.isFinite(expiresTime)
        && expiresTime > currentTimestampSeconds();
}

export function getCurrentPlanName(loginInformation) {
    if (!hasSubscriptionAccess(loginInformation)) {
        return 'Free';
    }
    const planType = String(loginInformation.planType).toLowerCase();
    if (planType === 'pro') {
        return 'Pro';
    }
    if (planType === 'premium') {
        return 'Premium';
    }
    return 'Free';
}

export function isSubscriptionExpired(loginInformation) {
    const iapStatus = Number(loginInformation == null ? NaN : loginInformation.iapStatus);
    const subscribedBefore = iapStatus === 3 || iapStatus === 4 || iapStatus === 5;
    return subscribedBefore && !hasSubscriptionAccess(loginInformation);
}

export function getActiveCompanyList(companyList) {
    return Array.isArray(companyList)
        ? companyList.filter(company => company != null && Number(company.syncStatus) !== 1)
        : [];
}

export function getPrimaryCompany(companyList) {
    const activeCompanies = getActiveCompanyList(companyList).slice();
    if (activeCompanies.length === 0) {
        return null;
    }
    activeCompanies.sort((left, right) => {
        const leftCreateDate = Number(left.createDate);
        const rightCreateDate = Number(right.createDate);
        if (!Number.isFinite(leftCreateDate) || !Number.isFinite(rightCreateDate)) {
            return 0;
        }
        const createDateDifference = leftCreateDate - rightCreateDate;
        if (createDateDifference !== 0) {
            return createDateDifference;
        }
        const leftId = Number(left.id);
        const rightId = Number(right.id);
        return Number.isFinite(leftId) && Number.isFinite(rightId) ? leftId - rightId : 0;
    });
    return activeCompanies[0];
}

export function getDocumentCreationAccess(loginInformation, companyList, currentCompany) {
    if (!hasSubscriptionAccess(loginInformation)) {
        return SUBSCRIPTION_ACCESS_PRO_REQUIRED;
    }
    const planType = String(loginInformation.planType == null ? '' : loginInformation.planType).toLowerCase();
    if (planType !== 'pro') {
        return SUBSCRIPTION_ACCESS_ALLOWED;
    }
    if (currentCompany == null || currentCompany.innerUUID == null) {
        return SUBSCRIPTION_ACCESS_ALLOWED;
    }
    const primaryCompany = getPrimaryCompany(companyList);
    if (primaryCompany == null || primaryCompany.innerUUID == null) {
        return SUBSCRIPTION_ACCESS_ALLOWED;
    }
    return primaryCompany.innerUUID === currentCompany.innerUUID
        ? SUBSCRIPTION_ACCESS_ALLOWED
        : SUBSCRIPTION_ACCESS_PREMIUM_REQUIRED;
}

export function getStoredDocumentCreationAccess() {
    return getDocumentCreationAccess(
        requireLoginInformation(),
        getStoredCompanyList(),
        getCurrentCompany()
    );
}

export function canCreateNewCompany(loginInformation) {
    return hasSubscriptionAccess(loginInformation)
        && String(loginInformation.planType).toLowerCase() === 'premium';
}

export function getStoredCurrentPlanName() {
    return getCurrentPlanName(requireLoginInformation());
}

function normalizeCurrentPlan(currentPlan) {
    return ['Free', 'Pro', 'Premium'].includes(currentPlan) ? currentPlan : 'Free';
}

function normalizeSelectedPlan(selectedPlan) {
    return ['Pro', 'Premium'].includes(selectedPlan) ? selectedPlan : 'Pro';
}

export function setFastSpringPageContext(currentPlan, selectedPlan) {
    const loginInformation = requireLoginInformation();
    const context = {
        userId: loginInformation.id == null ? '' : String(loginInformation.id),
        currentPlan: normalizeCurrentPlan(currentPlan),
        selectedPlan: normalizeSelectedPlan(selectedPlan)
    };
    sessionStorage.setItem(SUBSCRIPTION_PAGE_CONTEXT_KEY, JSON.stringify(context));
    return context;
}

export function getFastSpringPageContext() {
    const stored = sessionStorage.getItem(SUBSCRIPTION_PAGE_CONTEXT_KEY);
    if (stored == null || stored === '') {
        return {userId: '', currentPlan: 'Free', selectedPlan: 'Pro'};
    }
    try {
        const context = JSON.parse(stored);
        return {
            userId: context.userId == null ? '' : String(context.userId),
            currentPlan: normalizeCurrentPlan(context.currentPlan),
            selectedPlan: normalizeSelectedPlan(context.selectedPlan)
        };
    } catch (error) {
        reportHandledError(error, {
            module: 'subscription',
            operation: 'read_subscription_page_context'
        });
        return {userId: '', currentPlan: 'Free', selectedPlan: 'Pro'};
    }
}

export function getFastSpringPageUrl(currentPlan, selectedPlan) {
    setFastSpringPageContext(currentPlan, selectedPlan);
    return window.location.origin + '/#/subscription';
}

export function openFastSpringPage(currentPlan, selectedPlan) {
    window.location.href = getFastSpringPageUrl(currentPlan, selectedPlan);
}
